class NbhVariantPriceLabels {
  constructor(productInfo) {
    this.productInfo = productInfo;
    this.currency = productInfo.querySelector('[data-nbh-currency]')?.textContent?.trim() || 'DKK';
    this.refreshVariantData();
  }

  refreshVariantData() {
    try {
      this.variants = JSON.parse(this.productInfo.querySelector('[data-nbh-variant-data]')?.textContent || '[]');
    } catch (_error) {
      this.variants = [];
    }
  }

  mount() {
    if (this.productInfo.dataset.nbhProductEnhanced === 'true') return;
    this.productInfo.dataset.nbhProductEnhanced = 'true';
    if (!this.variants.length) return;

    this.variantPicker = this.productInfo.querySelector('variant-selects');
    this.render();
    this.productInfo.addEventListener('change', () => this.scheduleRender());
    this.productInfo.addEventListener('input', (event) => {
      if (event.target?.matches('[name="quantity"]')) this.scheduleRender();
    });

    this.productObserver = new MutationObserver(() => {
      const currentVariantPicker = this.productInfo.querySelector('variant-selects');
      if (currentVariantPicker === this.variantPicker) return;
      this.variantPicker = currentVariantPicker;
      this.scheduleRender();
    });
    this.productObserver.observe(this.productInfo, { childList: true, subtree: true });
  }

  scheduleRender() {
    if (this.renderFrame) return;
    this.renderFrame = requestAnimationFrame(() => {
      this.renderFrame = null;
      this.refreshVariantData();
      this.render();
    });
  }

  formatPrice(price) {
    const amount = Number(price || 0) / 100;
    const hasDecimals = Math.round(amount * 100) % 100 !== 0;
    return new Intl.NumberFormat(document.documentElement.lang || 'da-DK', {
      style: 'currency',
      currency: this.currency,
      minimumFractionDigits: hasDecimals ? 2 : 0,
      maximumFractionDigits: hasDecimals ? 2 : 0,
    }).format(amount);
  }

  getVisualColor(value = '') {
    const normalized = String(value).trim().toLocaleLowerCase('da-DK');
    const rules = [
      [/mørkebrun|dark brown/, '#5b4032'],
      [/sortmalet eg|sort eg|black oak/, '#3a332d'],
      [/røget eg|røget|smoked/, '#7b6653'],
      [/mælkehvid|milk white/, '#eee9dc'],
      [/creme|cream/, '#e9dfcf'],
      [/guld|gold/, '#b58a3a'],
      [/oliven|olive/, '#6f7454'],
      [/rosa|pink|rose/, '#c99691'],
      [/rust|kobber|copper/, '#a6552e'],
      [/sort|black/, '#24211f'],
      [/hvid|white/, '#f7f5f0'],
      [/brun|brown/, '#79563e'],
      [/lys eg|natur eg/, '#c3a278'],
      [/natur|oak|\beg\b/, '#b8956d'],
      [/blå|blue/, '#597588'],
      [/grøn|green/, '#68745d'],
      [/rød|red/, '#a94937'],
      [/grå|grey|gray/, '#8b8985'],
      [/beige|sand/, '#cdb99f'],
    ];
    return rules.find(([pattern]) => pattern.test(normalized))?.[1] || '';
  }

  clearColorFallback(thumb) {
    thumb.classList.remove('nbh-color-option-thumb--fallback', 'nbh-color-option-thumb--semantic');
    thumb.style.removeProperty('background');
    thumb.style.removeProperty('background-color');
    thumb.style.removeProperty('border');
    thumb.style.removeProperty('box-shadow');
  }

  applyColorFallback(thumb, value) {
    thumb.querySelector('img')?.remove();
    thumb.textContent = '';
    thumb.classList.add('nbh-color-option-thumb--fallback');

    const visualColor = this.getVisualColor(value);
    if (visualColor) {
      thumb.classList.add('nbh-color-option-thumb--semantic');
      thumb.style.background = visualColor;
      thumb.style.border = '1px solid rgba(35, 31, 28, .16)';
      thumb.style.boxShadow = 'inset 0 0 0 1px rgba(255, 255, 255, .24)';
    } else {
      thumb.classList.remove('nbh-color-option-thumb--semantic');
      thumb.style.removeProperty('background');
      thumb.style.removeProperty('border');
      thumb.style.removeProperty('box-shadow');
      thumb.textContent = String(value || '').slice(0, 1);
    }
  }

  getOptionName(fieldset) {
    const legend = fieldset?.querySelector('legend');
    if (!legend) return '';
    const firstTextNode = [...legend.childNodes].find((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
    return (firstTextNode?.textContent || legend.textContent || '').replace(':', '').trim();
  }

  getSelectedValues(fieldsets) {
    return fieldsets.map((fieldset) => fieldset.querySelector('input:checked, option:checked')?.value);
  }

  getSelectedVariant(fieldsets) {
    const selected = this.getSelectedValues(fieldsets);
    return this.variants.find((candidate) => candidate.options.every((value, index) => value === selected[index]));
  }

  findVariantForOption(fieldsets, optionIndex, value) {
    const selected = this.getSelectedValues(fieldsets);
    selected[optionIndex] = value;

    return (
      this.variants.find((candidate) => candidate.options.every((optionValue, index) => optionValue === selected[index])) ||
      this.variants.find((candidate) => candidate.options[optionIndex] === value)
    );
  }

  enhanceLegend(fieldset, value) {
    const legend = fieldset.querySelector('legend');
    if (!legend) return;

    let current = legend.querySelector('[data-nbh-option-current]');
    if (!current) {
      current = document.createElement('span');
      current.className = 'nbh-option-current';
      current.dataset.nbhOptionCurrent = '';
      legend.append(current);
    }
    const nextText = value ? `· ${value}` : '';
    if (current.textContent !== nextText) current.textContent = nextText;
  }

  enhanceColorOptions(fieldsets, colorFieldset) {
    const colorIndex = fieldsets.indexOf(colorFieldset);
    if (colorIndex < 0) return;

    colorFieldset.dataset.nbhColorSelector = '';
    const selectedColor = colorFieldset.querySelector('input:checked')?.value || '';
    this.enhanceLegend(colorFieldset, selectedColor);

    colorFieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
      const label = colorFieldset.querySelector(`label[for="${CSS.escape(input.id)}"]`);
      if (!label) return;

      const variant = this.findVariantForOption(fieldsets, colorIndex, input.value);
      const imageSrc = variant?.featured_image?.src || variant?.featured_media?.preview_image?.src || '';

      let thumb = label.querySelector('[data-nbh-color-thumb]');
      let name = label.querySelector('[data-nbh-color-name]');

      if (!thumb) {
        const unavailable = label.querySelector('.label-unavailable')?.cloneNode(true);
        label.textContent = '';

        thumb = document.createElement('span');
        thumb.className = 'nbh-color-option-thumb';
        thumb.dataset.nbhColorThumb = '';

        name = document.createElement('span');
        name.className = 'nbh-color-option-name';
        name.dataset.nbhColorName = '';
        name.textContent = input.value;

        label.append(thumb, name);
        if (unavailable) label.append(unavailable);
      }

      if (name && name.textContent !== input.value) name.textContent = input.value;

      const existingImage = thumb.querySelector('img');
      if (imageSrc) {
        this.clearColorFallback(thumb);
        if (!existingImage) {
          const image = document.createElement('img');
          image.alt = '';
          image.loading = 'lazy';
          image.width = 96;
          image.height = 96;
          image.src = imageSrc;
          thumb.textContent = '';
          thumb.append(image);
        } else if (existingImage.src !== imageSrc) {
          existingImage.src = imageSrc;
        }
      } else {
        this.applyColorFallback(thumb, input.value);
      }
    });
  }

  enhanceSizeOptions(fieldsets, sizeFieldset) {
    const selected = this.getSelectedValues(fieldsets);
    const sizeIndex = fieldsets.indexOf(sizeFieldset);
    if (sizeIndex < 0) return;

    sizeFieldset.dataset.nbhSizeSelector = '';
    this.enhanceLegend(sizeFieldset, selected[sizeIndex] || '');

    sizeFieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
      const options = [...selected];
      options[sizeIndex] = input.value;
      const variant = this.variants.find((candidate) => candidate.options.every((value, index) => value === options[index]));
      const label = sizeFieldset.querySelector(`label[for="${CSS.escape(input.id)}"]`);
      if (!label || !variant) return;

      let price = label.querySelector('[data-nbh-size-price]');
      if (!price) {
        price = document.createElement('span');
        price.dataset.nbhSizePrice = '';
        label.append(price);
      }
      const formattedPrice = this.formatPrice(variant.price);
      if (price.textContent !== formattedPrice) price.textContent = formattedPrice;
    });
  }

  updateProductState(variant, fieldsets) {
    const submitButton = this.productInfo.querySelector('[id^="ProductSubmitButton-"]');
    const availability = this.productInfo.querySelector('[data-nbh-availability]');
    const mainPrice = this.productInfo.querySelector('[data-nbh-main-price]');
    const comparePrice = this.productInfo.querySelector('[data-nbh-main-compare-price]');
    const selectedSizeTarget = this.productInfo.querySelector('[data-nbh-selected-size]');
    const priceContext = this.productInfo.querySelector('[data-nbh-price-context]');
    const priceContextPrefix = priceContext?.querySelector('span:first-child');
    const selectedVariantTargets = this.productInfo.querySelectorAll('[data-nbh-selected-variant]');
    const totalTarget = this.productInfo.querySelector('[data-nbh-product-total]');
    const quantityInput = this.productInfo.querySelector('[name="quantity"]');
    const quantity = Math.max(1, Number.parseInt(quantityInput?.value || '1', 10) || 1);
    const total = variant ? variant.price * quantity : 0;

    this.productInfo.dataset.nbhSelectedVariantId = variant?.id || '';

    if (mainPrice && variant) mainPrice.textContent = this.formatPrice(variant.price);

    if (comparePrice) {
      const hasComparePrice = Boolean(variant?.compare_at_price && variant.compare_at_price > variant.price);
      comparePrice.hidden = !hasComparePrice;
      if (hasComparePrice) comparePrice.textContent = this.formatPrice(variant.compare_at_price);
    }

    if (priceContextPrefix) {
      if (priceContextPrefix.textContent !== 'for') priceContextPrefix.textContent = 'for';
      priceContextPrefix.removeAttribute('aria-hidden');
    }

    if (totalTarget && variant) totalTarget.textContent = this.formatPrice(total);

    if (submitButton) {
      const label = submitButton.querySelector('span:not(.loading__spinner):not(.svg-wrapper)');
      if (label) {
        if (!variant) label.textContent = 'Ikke tilgængelig';
        else if (!variant.available) label.textContent = 'Udsolgt';
        else label.textContent = 'Læg i kurv';
      }

      let ctaPrice = submitButton.querySelector('[data-nbh-cta-price]');
      if (!ctaPrice) {
        ctaPrice = document.createElement('span');
        ctaPrice.dataset.nbhCtaPrice = '';
        submitButton.append(ctaPrice);
      }
      const priceText = variant?.available ? ` · ${this.formatPrice(total)}` : '';
      if (ctaPrice.textContent !== priceText) ctaPrice.textContent = priceText;
      ctaPrice.hidden = !variant?.available;
    }

    if (availability) {
      const isAvailable = Boolean(variant?.available);
      const backorderIds = (availability.dataset.backorderIds || '').split(',').map((id) => id.trim());
      const isBackorder = isAvailable && backorderIds.includes(String(variant.id));
      const availabilityText = isAvailable ? (isBackorder ? availability.dataset.backorderLabel : 'På lager') : variant ? 'Udsolgt' : 'Ikke tilgængelig';
      availability.dataset.available = String(isAvailable);
      if (availability.textContent.trim() !== availabilityText) availability.textContent = availabilityText;
    }

    const sizeIndex = fieldsets.findIndex((fieldset) => /størrelse|size/i.test(this.getOptionName(fieldset)));
    const selectedSize = sizeIndex >= 0 ? this.getSelectedValues(fieldsets)[sizeIndex] : '';
    if (selectedSizeTarget) selectedSizeTarget.textContent = selectedSize || '';
    if (priceContext) priceContext.hidden = !selectedSize;

    const variantTitle = variant?.title || this.getSelectedValues(fieldsets).filter(Boolean).join(' · ');
    selectedVariantTargets.forEach((target) => {
      if (target.textContent !== variantTitle) target.textContent = variantTitle;
    });
  }

  render() {
    const variantPicker = this.productInfo.querySelector('variant-selects');
    if (!variantPicker || !this.variants.length) return;
    this.variantPicker = variantPicker;

    const fieldsets = [...variantPicker.querySelectorAll('fieldset')];
    if (!fieldsets.length) return;

    const colorFieldset = fieldsets.find((fieldset) => /farve|color|finish|materiale|material|land|country/i.test(this.getOptionName(fieldset)));
    const sizeFieldset = fieldsets.find((fieldset) => /størrelse|size|højde|height|længde|length|diameter|dimension|^variant$/i.test(this.getOptionName(fieldset)));

    if (colorFieldset) this.enhanceColorOptions(fieldsets, colorFieldset);
    if (sizeFieldset) this.enhanceSizeOptions(fieldsets, sizeFieldset);

    const selectedVariant = this.getSelectedVariant(fieldsets);
    this.updateProductState(selectedVariant, fieldsets);
  }
}

function mountNbhProductEnhancements(root = document) {
  const productInfos = [];
  if (root instanceof Element && root.matches('[data-nbh-product-main]')) productInfos.push(root);
  root.querySelectorAll?.('[data-nbh-product-main]').forEach((element) => productInfos.push(element));

  productInfos.forEach((productInfo) => {
    if (productInfo.dataset.nbhProductEnhanced === 'true') return;
    new NbhVariantPriceLabels(productInfo).mount();
  });


}

mountNbhProductEnhancements(document);

const nbhProductDomObserver = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) mountNbhProductEnhancements(node);
    });
  });
});
nbhProductDomObserver.observe(document.documentElement, { childList: true, subtree: true });

if (!customElements.get('nbh-sticky-atc')) {
  customElements.define(
    'nbh-sticky-atc',
    class NbhStickyAtc extends HTMLElement {
      connectedCallback() {
        this.productInfo = this.closest('product-info');
        this.priceTarget = this.querySelector('[data-nbh-sticky-price]');
        this.variantTarget = this.querySelector('[data-nbh-sticky-variant]');
        this.button = this.querySelector('[data-nbh-sticky-submit]');

        this.button?.addEventListener('click', () => this.mainButton?.click());
        this.productInfo?.addEventListener('change', () => this.scheduleSync());
        this.productInfo?.addEventListener('input', (event) => {
          if (event.target?.matches('[name="quantity"]')) this.scheduleSync();
        });

        this.productObserver = new MutationObserver(() => this.scheduleSync());
        if (this.productInfo) {
          this.productObserver.observe(this.productInfo, {
            attributes: true,
            attributeFilter: ['disabled', 'aria-disabled'],
            childList: true,
            subtree: true,
          });
        }

        this.sync();
      }

      disconnectedCallback() {
        this.productObserver?.disconnect();
        this.visibilityObserver?.disconnect();
      }

      scheduleSync() {
        if (this.syncFrame) return;
        this.syncFrame = requestAnimationFrame(() => {
          this.syncFrame = null;
          this.sync();
        });
      }

      sync() {
        const currentMainButton = this.productInfo?.querySelector('[id^="ProductSubmitButton-"]');
        if (currentMainButton !== this.mainButton) {
          this.mainButton = currentMainButton;
          this.visibilityObserver?.disconnect();
          if (this.mainButton) {
            this.visibilityObserver = new IntersectionObserver(
              ([entry]) => {
                this.hidden = entry.isIntersecting;
              },
              { threshold: 0.35 }
            );
            this.visibilityObserver.observe(this.mainButton);
          }
        }

        const price = this.productInfo?.querySelector('[data-nbh-product-total]')?.textContent?.trim();
        const variantText = this.productInfo?.querySelector('[data-nbh-selected-variant]')?.textContent?.trim() ||
          [...(this.productInfo?.querySelectorAll('variant-selects input:checked, variant-selects option:checked') || [])]
            .map((input) => input.value)
            .filter(Boolean)
            .join(' · ');

        if (this.variantTarget && variantText && this.variantTarget.textContent !== variantText) this.variantTarget.textContent = variantText;
        if (this.priceTarget && price && this.priceTarget.textContent !== price) this.priceTarget.textContent = price;

        if (this.button && this.mainButton) {
          if (this.button.disabled !== this.mainButton.disabled) this.button.disabled = this.mainButton.disabled;
          const ariaDisabled = String(this.mainButton.disabled);
          if (this.button.getAttribute('aria-disabled') !== ariaDisabled) this.button.setAttribute('aria-disabled', ariaDisabled);
        }
      }
    }
  );
}
